import React, { Suspense, lazy, useContext } from "react";
// eslint-disable-next-line no-unused-vars
import { BrowserRouter as Router, Routes, Route, useLocation, useParams, Navigate } from "react-router-dom";
import GlobalTransitionLoader from "../Components/GlobalTransitionLoader/GlobalTransitionLoader";
import LandingPage from "../Pages/LandingPage/LandingPage";
import { CommonLayout } from "../Pages/Layout";
import MyContext from "../Context/MainContext";
import CarLoaderFull from "../Components/PageLoader/CarLoaderFull/CarLoaderFull";

// Guard for LandingPage so that single-segment paths that are not valid languages ("ar") or empty fall back to PageNotFound
const LandingRoute = () => {
  const { lang } = useParams();
  if (lang && lang !== "ar") {
    return <PageNotFound />;
  }
  return <LandingPage />;
};

const HomeRedirect = () => {
  const { lang } = useParams();
  return <Navigate to={lang === "ar" ? "/ar" : "/"} replace />;
};

// Secondary Route Components converted to lazy loading
const BranchPage = lazy(() => import("../Pages/BranchPage/BranchPage"));
const PageNotFound = lazy(() => import("../Pages/PageNotFound/PageNotFound"));
const PaymentSuccessful = lazy(() => import("../Pages/PaymentSuccessful/PaymentSuccessful"));
const PaymentFailed = lazy(() => import("../Pages/PaymentFailed/PaymentFailed"));
const ThankYou = lazy(() => import("../Pages/ThankYou/ThankYou"));
const Payment = lazy(() => import("../Pages/payment/Payment"));
const UnAuthorized = lazy(() => import("../Pages/UnAuhorized/UnAuthorized"));
const MotorVechileInfo = lazy(() => import("../Pages/MotorVechileInfo/MotorVechileInfo"));
const MotorValuation = lazy(() => import("../Pages/MotorValuation/MotorValuation"));
const MotorBestDeals = lazy(() => import("../Pages/MotorBestDeals/MotorBestDeals"));
const MotorAddons = lazy(() => import("../Pages/MotorAddons/MotorAddons"));
const MotorAddonsAdditionalDrivers = lazy(() => import("../Pages/MotorAddonsAdditionalDrivers/MotorAddonsAdditionalDrivers"));
const MotorAddVehicleOwnersName = lazy(() => import("../Pages/MotorAddVehicleOwnersName/MotorAddVehicleOwnersName"));
const MotorSummary = lazy(() => import("../Pages/MotorSummary/MotorSummary"));
// const MotorUploadPhotos = lazy(() => import("../Pages/MotorUploadPhotos/MotorUploadPhotos"));
const MotorUploadSuccess = lazy(() => import("../Pages/MotorUploadSuccess/MotorUploadSuccess"));
const TravelBasicForm = lazy(() => import("../Pages/TravelBasicForm/TravelBasicForm"));
const TravelBestDeals = lazy(() => import("../Pages/TravelBestDeals/TravelBestDeals"));
const TravellersDetails = lazy(() => import("../Pages/TravellersDetails/TravellersDetails"));
const TravelSummary = lazy(() => import("../Pages/TravelSummary/TravelSummary"));
const MotorPayment = lazy(() => import("../Pages/MotorPayment/MotorPayment"));
const MotorBasicForm = lazy(() => import("../Pages/MotorBasicForm/MotorBasicForm"));
const TravelPayment = lazy(() => import("../Pages/TravelPayment/TravelPayment"));
const DomesticBasicForm = lazy(() => import("../Pages/DomesticBasicForm/DomesticBasicForm"));
const DomesticBestDeals = lazy(() => import("../Pages/DomesticBestDeals/DomesticBestDeals"));
const DomesticSponsor = lazy(() => import("../Pages/DomesticSponsor/DomesticSponsor"));
const DomesticDocumentUpload = lazy(() => import("../Pages/DomesticDocumentUpload/DomesticDocumentUpload"));
const DomesticMedicalHistory = lazy(() => import("../Pages/DomesticMedicalHistory/DomesticMedicalHistory"));
const DomesticSummary = lazy(() => import("../Pages/DomesticSummary/DomesticSummary"));
const DomesticPayment = lazy(() => import("../Pages/DomesticPayment/DomesticPayment"));
const CorporateInsurance = lazy(() => import("../Pages/CorporateInsurance/CorporateInsurance"));
const AboutUs = lazy(() => import("../Pages/AboutUs/AboutUs"));
const FeedbackComplaints = lazy(() => import("../Pages/FeedbackComplaints/FeedbackComplaints"));
const ProductGallery = lazy(() => import("../Pages/ProductGallery/ProductGallery"));
const PersonalLinesOfInsurance = lazy(() => import("../Pages/PersonalLinesOfInsurance/PersonalLinesOfInsurance"));
const PrivacyPolicy = lazy(() => import("../Pages/PrivacyPolicy/PrivacyPolicy"));
const SeoMotorInsurance = lazy(() => import("../Pages/SeoMotorInsurance/MotorInsurance"));
const SeoTravelInsurance = lazy(() => import("../Pages/SeoTravelInsurance/TravelInsurance"));
const SeoHomeInsurance = lazy(() => import("../Pages/SeoHomeInsurance/HomeInsurance"));
const MotorFleetInsurance = lazy(() => import("../Pages/MotorFleetInsurance/MotorFleetInsurance"));
const TermsAndConditions = lazy(() => import("../Pages/TermsAndConditions/TermsAndConditions"));
const SeoHealthInsurance = lazy(() => import("../Pages/SeoHealthInsurance/SeoHealthInsurance"));
const Faq = lazy(() => import("../Pages/Faq/Faq"));
const ClaimsAssistance = lazy(() => import("../Pages/ClaimsAssistance/ClaimsAssistance"));
const MotorClaimProcess = lazy(() => import("../Pages/MotorClaimProcess/MotorClaimProcess"));
const TravelClaimProcess = lazy(() => import("../Pages/TravelClaimProcess/TravelClaimProcess"));
const MedicalClaimProcess = lazy(() => import("../Pages/MedicalClaimProcess/MedicalClaimProcess"));
const LifeInsuranceClaimProcess = lazy(() => import("../Pages/LifeInsuranceClaimProcess/LifeInsuranceClaimProcess"));
const OtherLineClaimProcess = lazy(() => import("../Pages/OtherLineClaimProcess/OtherLineClaimProcess"));
const HousemaidInsurance = lazy(() => import("../Pages/HousemaidInsurance/HousemaidInsurance"));
const PersonalAccidentInsurance = lazy(() => import("../Pages/PersonalAccidentInsurance/PersonalAccidentInsurance"));
const GolfInsurance = lazy(() => import("../Pages/GolfInsurance/GolfInsurance"));
const LiabilityInsurance = lazy(() => import("../Pages/SeoLiabilityInsurance/LiabilityInsurance"));
const ProjectInsurance = lazy(() => import("../Pages/ProjectInsurance/ProjectInsurance"));
const HauliersFullValueCargoInsurance = lazy(() => import("../Pages/SeoHauliersFullValueCargoInsurance/HauliersFullValueCargoInsurance"));
const StockThroughputCoverInsurance = lazy(() => import("../Pages/SeoStockThroughputCoverInsurance/StockThroughputCoverInsurance"));
const TransitInsurance = lazy(() => import("../Pages/SeoTransitInsurance/TransitInsurance"));
const WorkmenCompensationInsurance = lazy(() => import("../Pages/SeoWorkmenCompensationInsurance/WorkmenCompensationInsurance"));
const LifeInsurance = lazy(() => import("../Pages/LifeInsurance/LifeInsurance"));
const CreditLifeInsurance = lazy(() => import("../Pages/CreditLifeInsurance/CreditLifeInsurance"));
const RelocationInsurance = lazy(() => import("../Pages/RelocationInsurance/RelocationInsurance"));
const BoatInsurance = lazy(() => import("../Pages/BoatInsurance/BoatInsurance"));
const PaintInsurance = lazy(() => import("../Pages/PaintInsurance/PaintInsurance"));
const PropertyInsurance = lazy(() => import("../Pages/PropertyInsurance/PropertyInsurance"));
const GroupHealthInsurance = lazy(() => import("../Pages/GroupMedicalInsurance/GroupMedicalInsurance"));
const GroupPersonalAccidentCoverInsurance = lazy(() => import("../Pages/SeoGroupPersonalAccidentCoverInsurance/GroupPersonalAccidentCoverInsurance"));
const GroupLifeInsurance = lazy(() => import("../Pages/SeoGroupLifeInsurance/GroupLifeInsurance"));
const FidelityGuaranteeInsurance = lazy(() => import("../Pages/FidelityGuaranteeInsurance/FidelityGuaranteeInsurance"));
const BurglaryInsurance = lazy(() => import("../Pages/BurglaryInsurance/BurglaryInsurance"));
const JewelersBlockInsurance = lazy(() => import("../Pages/JewelersBlockInsurance/JewelersBlockInsurance"));
const EnergyInsurance = lazy(() => import("../Pages/EnergyInsurance/EnergyInsurance"));
const EventInsurance = lazy(() => import("../Pages/EventInsurance/EventInsurance"));
const CyberCrimeInsurance = lazy(() => import("../Pages/CyberCrimeInsurance/CyberCrimeInsurance"));
const TerrorismAndPoliticalRiskInsuranceCoverage = lazy(() => import("../Pages/TerrorismAndPoliticalRiskInsuranceCoverage/TerrorismAndPoliticalRiskInsuranceCoverage"));
const MotorUploadDocumentsBreakin = lazy(() => import("../Pages/MotorUploadDocumentsForBreakin/MotorUploadDocumentsForBreakin"));
const MotorLiveDocumentsBreakin = lazy(() => import("../Pages/MotorLiveDocumentsBreakin/MotorLiveDocumentsBreakin"));
const MotorLicenseInfo = lazy(() => import("../Pages/MotorLicenseInfo/MotorLicenseInfo"));
const Login = lazy(() => import("../Pages/Login/Login"));
const LoginDev = lazy(() => import("../Pages/Login/LoginDev"));

const Dashboard = lazy(() => import("../Pages/MsiDashboard/Dashboard/Dashboard"));
const EnquiryData = lazy(() => import("../Pages/MsiDashboard/EnquiryData/EnquiryData"));
const OrderData = lazy(() => import("../Pages/MsiDashboard/OrderData/OrderData"));
const PaymentData = lazy(() => import("../Pages/MsiDashboard/PaymentData/PaymentData"));
const PaymentDoneConflict = lazy(() => import("../Pages/MsiDashboard/PaymentDoneConflict/PaymentDoneConflict"));
const PolicyData = lazy(() => import("../Pages/MsiDashboard/PolicyData/PolicyData"));
const ViewLeads = lazy(() => import("../Pages/MsiDashboard/ViewLeads/ViewLeads"));
const OrderDetails = lazy(() => import("../Pages/MsiDashboard/OrderDetails/OrderDetails"));
const PolicyDetails = lazy(() => import("../Pages/MsiDashboard/PolicyDetails/PolicyDetails"));
const BranchesData = lazy(() => import("../Pages/MsiDashboard/BranchesData/BranchesData"));
const AdminLoginDummy = lazy(() => import("../Pages/MsiDashboard/AdminLogin/AdminLoginDummy"));
const AdminLogin = lazy(() => import("../Pages/MsiDashboard/AdminLogin/AdminLogin"));
const CommissionDashboard = lazy(() => import("../Pages/Commissions/CommissionDashboard/CommissionDashboard"));
const CommissionReport = lazy(() => import("../Pages/Commissions/CommissionReport/CommissionReport"));
const UnauthorizedAccess = lazy(() => import("../Pages/UnauthorizedAccess/UnauthorizedAccess"));
const CanceledData = lazy(() => import("../Pages/MsiDashboard/CanceledData/CanceledData"));
const PaymentsByChannel = lazy(() => import("../Pages/MsiDashboard/PaymentsByChannel/PaymentsByChannel"));
const SearchVehicleData = lazy(() => import("../Pages/MsiDashboard/SearchVehicleData/SearchVehicleData"));
const OpalDashboard = lazy(() => import("../Pages/MsiDashboard/OpalDashboard/OpalDashboard"));
const PolicyExpirySoon = lazy(() => import("../Pages/MsiDashboard/PolicyExpirySoon/PolicyExpirySoon"));
const QuotesDetails = lazy(() => import("../Pages/MsiDashboard/QuotesDetails/QuotesDetails"));
const BlogList = lazy(() => import("../Components/Blog/BlogList"));
const BlogDetail = lazy(() => import("../Components/Blog/BlogDetail"));




// const PublicRoutes = () =>{
//     return( <div>
//                 <Route exact path=""  element={<LandingPage />} />
//                 <Route exact path="term-insurance/:channel" element={<BasicForm />} />
//         </div>
//     );
// }
const PrivateRoutes = () => {
  const { ctLoading } = useContext(MyContext);
  const location = useLocation();

  if (/^\/en(?!ergy-insurance($|\/))/i.test(location.pathname)) {
    return <PageNotFound />;
  }

  // console.log("ctLoadingctLoading",ctLoading);
  return (
    <Routes>
      <Route path="/:lang?">
        <Route
          exact
          path=":insuranceType/:channel/payment"
          element={<Payment />}
        />
        <Route
          exact
          path=":insuranceType/:channel/payment-successful"
          element={<PaymentSuccessful />}
        />
        <Route
          exact
          path=":insuranceType/:channel/payment-failed"
          element={<PaymentFailed />}
        />
        <Route
          exact
          path=":insuranceType/:channel/thank-you"
          element={<ThankYou />}
        />
        <Route exact path="" element={<LandingRoute />} />
        <Route exact path="home" element={<HomeRedirect />} />
        <Route exact path="blogs" element={<BlogList />} />
        <Route exact path="blogs/category/:categorySlug" element={<BlogList />} />
        <Route exact path="blogs/tag/:tagSlug" element={<BlogList />} />
        <Route path="blogs/:slug" element={<BlogDetail />} />
        <Route exact path="login" element={<Login />} />
        <Route exact path="dev-login" element={<LoginDev />} />

        <Route exact path="branch" element={<BranchPage />} />
        <Route exact path="*" element={<PageNotFound />} />
        <Route exact path="unauthorized" element={<UnAuthorized />} />

        <Route
          path=":insuranceType/:channel/motor-basic-form"
          element={<MotorBasicForm />}
        //1
        />
        <Route
          exact
          path=":insuranceType/:channel/motor-vehicle-info"
          element={<MotorVechileInfo />}
        //2
        />
        <Route
          exact
          path=":insuranceType/:channel/vehicle-valuation"
          element={<MotorValuation />}
        //3 comprehensive
        />
        <Route
          exact
          path=":insuranceType/:channel/motor-licence-info"
          element={<MotorLicenseInfo />}
        // 4 only invalid licence
        />
        <Route
          exact
          path=":insuranceType/:channel/motor-best-deals"
          element={<MotorBestDeals />}
        // 3 third party  // 4 comprehensive
        />
        <Route
          exact
          path=":insuranceType/:channel/motor-addons"
          element={
            <CommonLayout>
              <MotorAddons />{" "}
            </CommonLayout>
          }
        />

        <Route
          exact
          path=":insuranceType/:channel/proposer-info"
          element={<MotorAddonsAdditionalDrivers />}
        // 5 
        />
        <Route
          exact
          path=":insuranceType/:channel/motor-add-vehicle-owners-name"
          element={
            <CommonLayout>
              <MotorAddVehicleOwnersName />{" "}
            </CommonLayout>
          }
        />
        <Route
          exact
          path=":insuranceType/:channel/motor-summary"
          element={<MotorSummary />}
        // 7
        />
        <Route
          exact
          path=":insuranceType/:channel/upload-documents"
          element={
            <CommonLayout>
              {!ctLoading || true ? (
                <MotorUploadDocumentsBreakin />
                // 6
              ) : (
                <CarLoaderFull />
              )}
            </CommonLayout>
          }
        />
        <Route
          exact
          path=":insuranceType/:channel/documents-upload"
          element={<MotorLiveDocumentsBreakin />}
        // 7
        />
        <Route
          path=":insuranceType/:channel/upload-success"
          element={<MotorUploadSuccess />}
        //after doc upload success
        />
        <Route
          exact
          path=":insuranceType/:channel/motor-payment"
          element={<MotorPayment />}
        />
        {/* Travel Routes */}
        <Route
          exact
          path=":insuranceType/:channel/travel-basic-form"
          element={<TravelBasicForm />}
        />
        <Route
          exact
          path=":insuranceType/:channel/travel-best-deals"
          element={<TravelBestDeals />}
        />
        <Route
          exact
          path=":insuranceType/:channel/travellers-details"
          element={<TravellersDetails />}
        />
        <Route
          exact
          path=":insuranceType/:channel/traveller-summary"
          element={<TravelSummary />}
        />
        <Route
          exact
          path=":insuranceType/:channel/travel-payment"
          element={<TravelPayment />}
        />
        {/* DOMESTIC INSURANCE ROUTES */}
        <Route
          exact
          path=":insuranceType/:channel/domestic-basic-form"
          element={<DomesticBasicForm />}
        />
        <Route
          exact
          path=":insuranceType/:channel/domestic-best-deals"
          element={<DomesticBestDeals />}
        />
        <Route
          exact
          path=":insuranceType/:channel/domestic-sponsor"
          element={<DomesticSponsor />}
        />
        <Route
          exact
          path=":insuranceType/:channel/domestic-document-upload"
          element={<DomesticDocumentUpload />}
        />
        <Route
          exact
          path=":insuranceType/:channel/domestic-medical-history"
          element={<DomesticMedicalHistory />}
        />
        <Route
          exact
          path=":insuranceType/:channel/domestic-summary"
          element={<DomesticSummary />}
        />
        <Route
          exact
          path=":insuranceType/:channel/domestic-payment"
          element={<DomesticPayment />}
        />

        <Route exact path="home-insurance" element={<SeoHomeInsurance />} />
        <Route
          exact
          path="corporate-insurance"
          element={<CorporateInsurance />}
        />
        <Route exact path="about-us" element={<AboutUs />} />
        {/* <Route exact path=":lang/about-us" element={<AboutUs />} /> */}

        <Route
          exact
          path="feedback-complaints"
          element={<FeedbackComplaints />}
        />
        <Route exact path="product-gallery" element={<ProductGallery />} />
        <Route
          exact
          path="personal-lines-of-insurance"
          element={<PersonalLinesOfInsurance />}
        />
        <Route exact path="privacy-policy" element={<PrivacyPolicy />} />
        <Route exact path="motor-insurance" element={<SeoMotorInsurance />} />
        <Route exact path="travel-insurance" element={<SeoTravelInsurance />} />
        <Route exact path="health-insurance" element={<SeoHealthInsurance />} />
        <Route
          exact
          path="motor-fleet-insurance"
          element={<MotorFleetInsurance />}
        />
        <Route
          exact
          path="housemaid-insurance"
          element={<HousemaidInsurance />}
        />
        <Route
          exact
          path="personal-accident-insurance"
          element={<PersonalAccidentInsurance />}
        />
        <Route exact path="golf-insurance" element={<GolfInsurance />} />
        <Route
          exact
          path="liability-insurance"
          element={<LiabilityInsurance />}
        />
        <Route exact path="project-insurance" element={<ProjectInsurance />} />
        <Route
          exact
          path="hauliers-full-value-cargo-cover-insurance"
          element={<HauliersFullValueCargoInsurance />}
        />
        <Route
          exact
          path="stock-throughput-cover-insurance"
          element={<StockThroughputCoverInsurance />}
        />
        <Route exact path="transit-insurance" element={<TransitInsurance />} />
        <Route
          exact
          path="workmen-compensation-insurance"
          element={<WorkmenCompensationInsurance />}
        />
        <Route exact path="life-insurance" element={<LifeInsurance />} />
        <Route exact path="credit-life-insurance" element={<CreditLifeInsurance />} />
        <Route
          exact
          path="relocation-insurance"
          element={<RelocationInsurance />}
        />
        <Route exact path="yacht-insurance" element={<BoatInsurance />} />
        <Route exact path="art-insurance" element={<PaintInsurance />} />
        <Route exact path="property-insurance" element={<PropertyInsurance />} />
        <Route
          exact
          path="group-health-insurance"
          element={<GroupHealthInsurance />}
        />
        <Route
          exact
          path="group-personal-accident-cover-insurance"
          element={<GroupPersonalAccidentCoverInsurance />}
        />
        <Route
          exact
          path="group-life-insurance"
          element={<GroupLifeInsurance />}
        />

        <Route
          exact
          path="terms-and-conditions"
          element={<TermsAndConditions />}
        />
        <Route exact path="faq" element={<Faq />} />
        <Route exact path="claims-assistance" element={<ClaimsAssistance />} />
        <Route
          exact
          path="claims-assistance/motor-claim-process"
          element={<MotorClaimProcess />}
        />
        <Route
          exact
          path="claims-assistance/travel-claim-process"
          element={<TravelClaimProcess />}
        />
        <Route
          exact
          path="claims-assistance/medical-claim-process"
          element={<MedicalClaimProcess />}
        />
        <Route
          exact
          path="claims-assistance/life-insurance-claim-process"
          element={<LifeInsuranceClaimProcess />}
        />
        <Route
          exact
          path="claims-assistance/other-line-claim-process"
          element={<OtherLineClaimProcess />}
        />
        <Route
          exact
          path="fidelity-guarantee-insurance"
          element={<FidelityGuaranteeInsurance />}
        />
        <Route exact path="burglary-insurance" element={<BurglaryInsurance />} />
        <Route
          exact
          path="jewelers-block-insurance"
          element={<JewelersBlockInsurance />}
        />
        <Route exact path="energy-insurance" element={<EnergyInsurance />} />
        <Route exact path="event-insurance" element={<EventInsurance />} />
        <Route
          exact
          path="cyber-crime-insurance"
          element={<CyberCrimeInsurance />}
        />
        <Route
          exact
          path="terrorism-and-political-risk-insurance-coverage"
          element={<TerrorismAndPoliticalRiskInsuranceCoverage />}
        />

        {/* mis dashboard Routes start*/}
        {/* <Route exact path="" element={<AdminLogin />} /> */}

        <Route exact path="dashboard" element={<Dashboard />} />
        <Route exact path="dashboard/login" element={<AdminLogin />} />
        <Route exact path="dashboard/enquiry" element={< EnquiryData />} />
        <Route exact path="dashboard/quotes-details" element={<QuotesDetails />} />
        <Route exact path="dashboard/application" element={<OrderData />} />
        <Route exact path="dashboard/payments" element={<PaymentData />} />
        <Route exact path="dashboard/payment-done-conflict" element={<PaymentDoneConflict />} />
        <Route exact path="dashboard/payments-by-channel" element={<PaymentsByChannel />} />
        <Route exact path="dashboard/policies" element={<PolicyData />} />
        <Route exact path="dashboard/policy-expiry-soon" element={<PolicyExpirySoon />} />
        <Route exact path="dashboard/cancelled" element={<CanceledData />} />
        <Route exact path="dashboard/view-leads" element={<ViewLeads />} />
        <Route exact path="dashboard/order-details" element={<OrderDetails />} />
        <Route exact path="dashboard/policy-details" element={<PolicyDetails />} />
        <Route exact path="dashboard/branches" element={<BranchesData />} />
        <Route exact path="dashboard/vehicle-search" element={<SearchVehicleData />} />
        <Route exact path="dashboard/opal-dashboard" element={<OpalDashboard />} />
        <Route exact path="dashboard/dev-login" element={<AdminLoginDummy />} />

        {/* mis dashboard Routes End */}

        {/* erp portal commission report route start */}
        <Route exact path="commissions" element={<CommissionDashboard />} />
        <Route exact path="commissions/login" element={<AdminLogin />} />
        <Route exact path="commissions/dev-login" element={<AdminLoginDummy />} />
        <Route exact path="commissions/commission-report" element={<CommissionReport />} />
        {/* erp portal commission report route end */}


        {/* UnauthorizedAccess page */}
        <Route
          exact
          path="unauthorized-access"
          element={<UnauthorizedAccess />}
        />
      </Route>
    </Routes>
  );
};
const RoutesComponent = () => {

  return (
    <div>
      <Router>
        <GlobalTransitionLoader />
        <div className="App">
          <Suspense fallback={<CarLoaderFull />}>
            <PrivateRoutes />
          </Suspense>
        </div>
      </Router>
    </div>
  );
};

export default RoutesComponent;
